
import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useUserStore } from "@/store/userStore";
import { useAuth } from "@/contexts/AuthContext";
// Wrapper renvoyant l'enveloppe { data, error } attendue ci-dessous
// (userProfileService.getUserProfile renvoie le profil brut → data undefined).
import { getUserProfile } from "@/services/edgeFunctionWrappers/profilesManagementWrapper";
import { ProfileHeader } from "@/components/profile/ProfileHeader";
import { ProfileTabs } from "@/components/profile/ProfileTabs";
import { ProfilePersonalInfo } from "@/components/profile/ProfilePersonalInfo";
import { ProfileEmergencyContact } from "@/components/profile/ProfileEmergencyContact";
import { ProfileMedicalForm } from "@/components/profile/ProfileMedicalForm";
import { ProfilePaymentMethods } from "@/components/profile/ProfilePaymentMethods";
import { ProfileInvoices } from "@/components/profile/ProfileInvoices";
import { ProfileSettings } from "@/components/profile/ProfileSettings";
import { toast } from "sonner";
import { useThemeCustomization } from "@/hooks/useThemeCustomization";
import { Button } from "@/components/ui/button";
import { FileText, HeartPulse, UserRound } from "lucide-react";

const UserProfile: React.FC = () => {
  const { t } = useTranslation(["profile", "common"]);
  const { user } = useAuth();
  const { role } = useUserStore();
  const { athleteTheme } = useThemeCustomization();
  const [profileData, setProfileData] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("personal");
  const [infoSection, setInfoSection] = useState<"personal" | "emergency" | "medical">("personal");
  const isVb20Athlete = role === "athlete" && athleteTheme.variant === "dark-premium";
  const isVb20Coach = role === "coach" && athleteTheme.variant === "dark-premium";
  const isPremiumProfile = isVb20Athlete || isVb20Coach;

  useEffect(() => {
    const loadProfileData = async () => {
      if (user?.id) {
        try {
          setIsLoading(true);
          // getUserProfile renvoie une enveloppe { data, error } : stocker
          // l'enveloppe entière rendait profileData.first_name toujours
          // undefined — les formulaires du profil s'affichaient VIDES quelles
          // que soient les données enregistrées.
          const { data, error } = await getUserProfile(user.id);
          if (error) throw error;
          setProfileData(data);
        } catch (error) {
          console.error("Error loading profile data:", error);
          toast.error(t("errors.loadingProfile", { ns: "profile" }));
        } finally {
          setIsLoading(false);
        }
      }
    };

    loadProfileData();
  }, [user?.id, t]);

  const handleTabChange = (tabId: string) => {
    setActiveTab(tabId);
  };

  const renderTabContent = () => {
    if (isLoading) return <div className="p-6 text-center">{t("loading", { ns: "common" })}</div>;
    
    switch (activeTab) {
      case "personal":
        if (!isPremiumProfile) return <ProfilePersonalInfo userId={user?.id} profileData={profileData} />;
        return (
          <div className="space-y-3">
            <div className="grid grid-cols-3 gap-2">
              <Button className="h-auto min-h-12 whitespace-normal px-1 text-[9px] leading-tight" variant={infoSection === "personal" ? "default" : "outline"} size="sm" onClick={() => setInfoSection("personal")}><UserRound />{t("tabs.personal")}</Button>
              <Button className="h-auto min-h-12 whitespace-normal px-1 text-[9px] leading-tight" variant={infoSection === "emergency" ? "default" : "outline"} size="sm" onClick={() => setInfoSection("emergency")}><HeartPulse />{t("tabs.emergency")}</Button>
              <Button className="h-auto min-h-12 whitespace-normal px-1 text-[9px] leading-tight" variant={infoSection === "medical" ? "default" : "outline"} size="sm" onClick={() => setInfoSection("medical")}><FileText />{t("tabs.medical")}</Button>
            </div>
            {infoSection === "personal" && <ProfilePersonalInfo userId={user?.id} profileData={profileData} email={user?.email} />}
            {infoSection === "emergency" && <ProfileEmergencyContact userId={user?.id} profileData={profileData} />}
            {infoSection === "medical" && <ProfileMedicalForm userId={user?.id} profileData={profileData} />}
          </div>
        );
      case "emergency":
        return <ProfileEmergencyContact userId={user?.id} profileData={profileData} />;
      case "medical":
        return <ProfileMedicalForm userId={user?.id} profileData={profileData} />;
      case "payment":
        return <ProfilePaymentMethods userId={user?.id} />;
      case "invoices":
        return <ProfileInvoices userId={user?.id} />;
      case "settings":
        return <ProfileSettings />;
      default:
        return <ProfilePersonalInfo userId={user?.id} profileData={profileData} />;
    }
  };

  return (
    <div className={`min-h-screen bg-background animate-fade-in ${role === 'athlete' ? 'athlete-page-bg' : ''} ${role === 'coach' ? 'coach-page-bg coach-profile-page' : ''}`}>
      <div className={`mx-auto max-w-5xl ${isPremiumProfile ? "pb-24 md:px-6 md:py-5" : "p-4 md:p-6"}`}>
        <ProfileHeader 
          profileData={profileData} 
          userId={user?.id || ''}
          isAdmin={role === 'admin'} 
          isCoach={role === 'coach'} 
        />
        
        <ProfileTabs 
          activeTab={activeTab} 
          onTabChange={handleTabChange}
          isAdmin={role === 'admin'} 
          isCoach={role === 'coach'} 
        />
        
        <div className={isPremiumProfile ? "px-3 pb-5" : "mt-6"}>
          {renderTabContent()}
        </div>
      </div>
    </div>
  );
};

export default UserProfile;
